import {AlertColor, Box, Checkbox, Container, FormControl, Grid, IconButton, Menu, MenuItem, Paper, Typography, Tooltip, useTheme} from '@mui/material';
import axios from 'axios';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {SettingsOutlined, FileDownloadOutlined} from '@mui/icons-material';
import strings from '../../../common/Translation/Translate';
import Notification from '../../../components/Notification';
import PageLoader from '../../../components/PageLoader';
import Config from '../../../utils/Config';
import axiosInstance from '../../../utils/axios';
import {identifiers} from '../../../utils/constants/identifiers';
import {URLS} from '../../../utils/constants/urls';
import {fetchFromStorage} from '../../../utils/storage';
import {createDropdownData, downloadExportFile, getMessage} from '../../../utils/validators/HelperFunctions';
import CompanyWiseBobChart from './CompanyWiseBobChart';
import Filter from './Filters';
import BudgetOverviewChart from './BudgetOverviewChart';
import Select, {components, StylesConfig} from 'react-select';
import {useSelector} from 'react-redux';
import {organizationInfo} from 'utils/redux/reducer/authentication-slice';
import {
    reportChartCellFullWidthSx,
    reportChartCellInGridSx,
    reportChartGridItemSx,
} from './reportChartLayout';
import ConsolidatedRevenueByOrgTable, {
    ConsolidatedComparisonMode,
    LeaderboardInsightsApi,
    OrgRevenueRow,
} from './ConsolidatedRevenueByOrgTable';
import ConsolidatedRevenueSummaryBanner from './ConsolidatedRevenueSummaryBanner';
import PortfolioMixAnalysis from './PortfolioMixAnalysis';
import ReportChartEnlargeDialog from './ReportChartEnlargeDialog';
import SourceActualizationProgress from './SourceActualizationProgress';
import OrganizationHealthMatrix from './OrganizationHealthMatrix';
import ContributionTrendsChart, {ContributionTrendsData} from './ContributionTrendsChart';

dayjs.extend(isSameOrAfter);

const REPORT_EMPTY_SX = {
    p: {xs: 1.25, sm: 2.5},
    borderRadius: 2,
    border: '1px dashed',
    borderColor: 'divider',
    bgcolor: '#fafbfc',
} as const;

/** JSON body for POST `/admin/consolidated-reports/{revenue-overview,contribution-trends,leaderboard-insights}`. */
type ConsolidatedPortfolioQueriesBody = {
    startDate: string;
    endDate: string;
    compareMode: ConsolidatedComparisonMode;
    organizationIds: string;
    lang: string;
};

function readLangFromStorage(): string {
    try {
        const stored = fetchFromStorage(identifiers.selectedLanguage);
        if (!stored) return 'en_IN';
        const parsed = JSON.parse(stored as string);
        return parsed?.value ?? 'en_IN';
    } catch {
        return 'en_IN';
    }
}

function buildConsolidatedPortfolioQueriesBody(
    dates: { startDate: string; endDate: string },
    comparisonMode: ConsolidatedComparisonMode,
    selectedOrgs: any[],
): ConsolidatedPortfolioQueriesBody {
    return {
        startDate: dates.startDate,
        endDate: dates.endDate,
        compareMode: comparisonMode,
        organizationIds: selectedOrgs.map((item: any) => String(item.value)).join(','),
        lang: readLangFromStorage(),
    };
}

/** Stable when mapped org list is unchanged (avoids re-sync loops when Redux passes a new array reference). */
function fingerprintMappedOrganizations(organizations: unknown): string {
    if (!Array.isArray(organizations) || organizations.length === 0) {
        return '__empty__';
    }
    return (organizations as { id?: number; name?: string }[])
        .map(o => `${Number(o?.id) || 0}\u0000${String(o?.name ?? '')}`)
        .sort()
        .join('\u0001');
}

const Option = (props: any) => {
    const theme = useTheme();
    return (
        <div>
            <components.Option {...props}>
                <Checkbox sx={{color: theme.palette.primary.main}} checked={props.isSelected}
                          onChange={() => null}/>{' '}
                <label>{props.label}</label>
            </components.Option>
        </div>
    );
};

const ConsolidatedReports: FC = () => {
    const org = useSelector(organizationInfo) as any;
    const [pageResponse, setPageResponse] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [filterResetKey, setFilterResetKey] = useState(0);
    const [revenueByOrgResponse, setRevenueByOrgResponse] = useState<{
        organizations: OrgRevenueRow[];
        bookingStatusCounts?: { name: string; value: number }[];
        portfolioActualizedRevenue?: number | null;
        portfolioActualizedRevenuePreviousPeriod?: number | null;
        portfolioActualizedCumulativeTrend?: { date: string; value: number }[];
    } | null>(null);
    const [isRevenueOverviewLoading, setIsRevenueOverviewLoading] = useState<boolean>(true);
    const [contributionTrends, setContributionTrends] = useState<ContributionTrendsData | null>(null);
    const [leaderboardInsights, setLeaderboardInsights] = useState<LeaderboardInsightsApi | null>(null);
    const [selectedOrgs, setSelectedOrgs] = useState<any[]>([]);
    const [message, setMessage] = useState<{
        display: boolean;
        severity: AlertColor | null;
        message: string;
    }>({
        display: false,
        severity: null,
        message: '',
    });

    const [comparisonMode, setComparisonMode] = useState<ConsolidatedComparisonMode>('PRIOR_PERIOD');
    const [comparisonMenuAnchor, setComparisonMenuAnchor] = useState<null | HTMLElement>(null);
    const [enlargedPortfolioChart, setEnlargedPortfolioChart] = useState<null | 'company' | 'actualization'>(null);

    const [dates, setDates] = useState<any>({
        startDate: dayjs().date(1).format('YYYY-MM-DD'),
        endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    });

    const mappedOrganizationsFingerprint = useMemo(
        () => fingerprintMappedOrganizations(org?.organizations),
        [org?.organizations],
    );

    // Intentionally depend on content fingerprint only; org?.organizations often gets a new array reference per render.
    useEffect(() => {
        setSelectedOrgs(prev => {
            const next = createDropdownData(org?.organizations, ['id', 'name']);
            if (prev.length !== next.length) {
                return next;
            }
            for (let i = 0; i < prev.length; i++) {
                if (prev[i]?.value !== next[i]?.value || prev[i]?.label !== next[i]?.label) {
                    return next;
                }
            }
            return prev;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mappedOrganizationsFingerprint tracks org?.organizations content
    }, [mappedOrganizationsFingerprint]);

    const selectedOrgsRef = useRef(selectedOrgs);
    selectedOrgsRef.current = selectedOrgs;

    const selectedOrgIdsKey = useMemo(
        () =>
            [...selectedOrgs]
                .map((item: any) => Number(item?.value))
                .filter(n => !Number.isNaN(n))
                .sort((a, b) => a - b)
                .join(','),
        [selectedOrgs],
    );

    const theme = useTheme();

    const handleCatch = useCallback((error: any) => {
        setMessage({
            display: true,
            severity: 'error',
            message: error.response?.data?.error_description ?? error.response?.data?.message ?? error.message,
        });
    }, []);

    const { startDate, endDate } = dates;

    const fetchConsolidatedRevenueOverview = useCallback(async () => {
        const orgs = selectedOrgsRef.current;
        if (orgs.length === 0) {
            setIsRevenueOverviewLoading(false);
            setRevenueByOrgResponse(null);
            setContributionTrends(null);
            setLeaderboardInsights(null);
            return;
        }
        setIsRevenueOverviewLoading(true);
        try {
            const body = buildConsolidatedPortfolioQueriesBody(
                { startDate, endDate },
                comparisonMode,
                orgs,
            );
            const [revResult, contribResult, insightsResult] = await Promise.allSettled([
                axiosInstance.post(URLS.consolidatedReportsRevenueOverview, body),
                axiosInstance.post(URLS.consolidatedReportsContributionTrends, body),
                axiosInstance.post(URLS.consolidatedReportsLeaderboardInsights, body),
            ]);
            if (revResult.status === 'fulfilled') {
                setRevenueByOrgResponse(revResult.value.data);
            } else {
                handleCatch(revResult.reason);
            }
            if (contribResult.status === 'fulfilled') {
                setContributionTrends(contribResult.value.data as ContributionTrendsData);
            } else {
                handleCatch(contribResult.reason);
                setContributionTrends(null);
            }
            if (insightsResult.status === 'fulfilled') {
                setLeaderboardInsights(insightsResult.value.data as LeaderboardInsightsApi);
            } else {
                handleCatch(insightsResult.reason);
                setLeaderboardInsights(null);
            }
        } catch (e: any) {
            handleCatch(e);
        } finally {
            setIsRevenueOverviewLoading(false);
        }
    }, [startDate, endDate, comparisonMode, handleCatch]);

    useEffect(() => {
        fetchConsolidatedRevenueOverview();
    }, [fetchConsolidatedRevenueOverview, selectedOrgIdsKey]);

    const runConsolidatedChartsSearch = async (
        useDates: { startDate: string; endDate: string },
        orgs: any[],
    ) => {
        try {
            const sendData = {
                startDate: dayjs(useDates.startDate).format('DD-MM-YYYY'),
                endDate: dayjs(useDates.endDate).format('DD-MM-YYYY'),
                orgIds: orgs.map((item: any) => item.value),
            };
            if (dayjs(useDates.endDate).isSameOrAfter(dayjs(useDates.startDate))) {
                setLoading(true);
                const {data} = await axiosInstance.post(URLS.consolidatedReports, sendData);
                setLoading(false);
                setPageResponse(data);
            } else {
                setMessage({
                    display: true,
                    severity: 'warning',
                    message: strings.common_endDate_error_message,
                });
            }
        } catch (error: any) {
            setLoading(false);
        }
    };

    const handleFilter = async (overrideDates?: { startDate: string; endDate: string }) => {
        await runConsolidatedChartsSearch(overrideDates || dates, selectedOrgs);
    };

    useEffect(() => {
        void handleFilter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = () => {
        if (selectedOrgs.length === 0) {
            setMessage({
                display: true,
                severity: 'error',
                message: strings.selectAtLeastOneOrg,
            });
            return;
        }
        if (dayjs(dates.endDate).isBefore(dayjs(dates.startDate))) {
            setMessage({
                display: true,
                severity: 'warning',
                message: strings.common_endDate_error_message,
            });
            return;
        }

        const token = fetchFromStorage(identifiers.access_token);
        try {
            setLoading(true);
            axios({
                url: `${Config.BaseURL + URLS.reportsExport('ALL')}`,
                method: 'POST',
                data: {
                    startDate: dayjs(dates.startDate).format('DD-MM-YYYY'),
                    endDate: dayjs(dates.endDate).format('DD-MM-YYYY'),
                    orgIds: selectedOrgs.map((item: any) => item.value),
                },
                headers: {Authorization: `Bearer ${token}`},
                responseType: 'blob',
            })
                .then(response => {
                    setLoading(false);
                    downloadExportFile(response, 'xlsx', `Bookings_${dates.startDate}_${dates.endDate}`);
                })
                .catch(err => {
                    setLoading(false);
                    if (err?.response?.status === 403) {
                        setMessage({
                            display: true,
                            severity: 'error',
                            message: strings.forbidden,
                        });
                    }
                });
        } catch (error: any) {
            handleCatch(error);
        } finally {
            setLoading(false);
        }
    };

    const closeNotification = (value: boolean) => setMessage(prev => ({...prev, display: value}));

    const handleDates = (date: { startDate: string; endDate: string }) => {
        setDates(date);
    };

    const onFilterSearch = (overrideDates?: { startDate: string; endDate: string }) => {
        if (selectedOrgs.length === 0) {
            setMessage({
                display: true,
                severity: 'error',
                message: strings.selectAtLeastOneOrg,
            });
            return;
        }
        void handleFilter(overrideDates);
    };

    const handleReset = () => {
        const nextDates = {
            startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
            endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
        };
        const nextOrgs = org?.organizations?.length ? createDropdownData(org.organizations, ['id', 'name']) : [];
        setDates(nextDates);
        setSelectedOrgs(nextOrgs);
        setFilterResetKey(k => k + 1);
        void runConsolidatedChartsSearch(nextDates, nextOrgs);
    };

    const bookingStripeCounts = useMemo(() => {
        const order = ['Confirmed', 'Tentative', 'Waitlisted', 'Cancelled', 'RFQ'];
        const raw = revenueByOrgResponse?.bookingStatusCounts ?? [];
        return order.map(label => {
            const found = raw.find((b: { name?: string }) => (b.name || '').toLowerCase() === label.toLowerCase());
            return {name: label, value: Number(found?.value) || 0};
        });
    }, [revenueByOrgResponse?.bookingStatusCounts]);

    const handleExport = (type: string) => {
        const token = fetchFromStorage(identifiers.access_token);
        setLoading(true);

        let filename = 'Revenue_Snapshots';
        axios({
            url: `${Config.BaseURL + URLS.consolidatedReportsExport(type)}`,
            method: 'POST',
            headers: {Authorization: `Bearer ${token}`},
            responseType: 'blob',
            data: {
                startDate: dayjs(dates.startDate).format('DD-MM-YYYY'),
                endDate: dayjs(dates.endDate).format('DD-MM-YYYY'),
                orgIds: selectedOrgs.map((item: any) => item.value),
            },
        })
            .then(response => {
                setLoading(false);
                const match = RegExp(/filename="?(.+?)"?$/).exec(response.headers['content-disposition']);
                if (match) {
                    filename = match[1];
                }
                downloadExportFile(response, 'xlsx', filename);
                setMessage({
                    display: true,
                    severity: 'success',
                    message: 'Report exported successfully',
                });
            })
            .catch(err => {
                setMessage({
                    display: true,
                    severity: 'error',
                    message: getMessage(err),
                });
            });
    };
    const colourStyles: StylesConfig<any> = {
        option: (styles, {data, isDisabled, isFocused, isSelected}) => {
            return {
                ...styles,
                zIndex: 200,
                backgroundColor: 'white',
                color: theme.palette.primary.main,
                ':hover': {
                    ...styles[':hover'],
                    backgroundColor: 'lightgrey',
                },
            };
        },
        menu: styles => ({
            ...styles,
            zIndex: 9999,
        }),
        menuList: styles => ({
            ...styles,
            zIndex: 9999,
        }),
    };

    const hasMappedOrgs = Boolean(org?.organizations?.length);
    const overviewRowsEmpty =
        !isRevenueOverviewLoading && !(revenueByOrgResponse?.organizations?.length ?? 0);

    const comparisonModeSummary =
        comparisonMode === 'YOY'
            ? 'Same period last year'
            : 'Previous equivalent period';

    const growthComparisonTrailingActions = (
        <>
            <Tooltip title={`Growth vs: ${comparisonModeSummary}. Open to change.`} placement="top">
                <IconButton
                    size="small"
                    aria-label="Growth comparison settings"
                    onClick={e => setComparisonMenuAnchor(e.currentTarget)}
                    sx={{
                        height: 40,
                        width: 40,
                        borderRadius: 1,
                        border: '1px solid #cfd8dc',
                        color: '#546e7a',
                        '&:hover': { backgroundColor: '#fafafa', borderColor: '#90a4ae' },
                    }}>
                    <SettingsOutlined sx={{ fontSize: 20 }} />
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={comparisonMenuAnchor}
                open={Boolean(comparisonMenuAnchor)}
                onClose={() => setComparisonMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: { mt: 0.5, maxWidth: 320, borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
                    },
                }}>
                <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
                        Period revenue & growth use your date filter. Compare growth to:
                    </Typography>
                </Box>
                <MenuItem
                    selected={comparisonMode === 'PRIOR_PERIOD'}
                    onClick={() => {
                        setComparisonMode('PRIOR_PERIOD');
                        setComparisonMenuAnchor(null);
                    }}
                    sx={{ fontSize: '0.82rem', py: 1 }}>
                    Previous equivalent period
                </MenuItem>
                <MenuItem
                    selected={comparisonMode === 'YOY'}
                    onClick={() => {
                        setComparisonMode('YOY');
                        setComparisonMenuAnchor(null);
                    }}
                    sx={{ fontSize: '0.82rem', py: 1 }}>
                    Same period last year
                </MenuItem>
            </Menu>
        </>
    );

    return (
        <Container maxWidth={false} sx={{ backgroundColor: '#F8FAFC', minHeight: '100vh', px: {xs: 0, sm: 3} }}>
            {loading && <PageLoader />}
            {message.display && (
                <Notification
                    isOpen={message.display}
                    message={message.message}
                    severity={message.severity as AlertColor}
                    closeNotification={closeNotification}
                />
            )}

            <Typography sx={{ fontSize: {xs: 20, sm: 24}, fontWeight: 700, mb: 2 }}>
                {strings.consolidated_reports_title_text}
            </Typography>

            {/* Section 1 — organizations, date range, search, reset, export */}
            <Box
                sx={{
                    mb: 3,
                    p: {xs: 1.25, sm: 2.5},
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: '#fff',
                    boxShadow: '0 1px 3px rgba(16, 24, 40, 0.06)',
                    position: 'relative',
                    zIndex: 2,
                }}>
                <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ mb: 1.25 }}>
                    {strings.selectAnOrg}
                </Typography>
                <FormControl
                    fullWidth
                    sx={{
                        mb: 2.25,
                        position: 'relative',
                        zIndex: 9998,
                        "div[class$ = 'control']": {
                            padding: '5px 0px 0px 0px !important',
                        },
                    }}>
                    <Select
                        styles={colourStyles}
                        hideSelectedOptions={false}
                        className="select"
                        options={createDropdownData(org?.organizations, ['id', 'name'])}
                        isMulti={true}
                        isSearchable={true}
                        isClearable={true}
                        value={selectedOrgs}
                        placeholder={strings.selectAnOrg}
                        closeMenuOnSelect={false}
                        onChange={(value: any) => {
                            setSelectedOrgs(value || []);
                        }}
                        components={{
                            Option,
                        }}
                    />
                </FormControl>
                <Filter
                    resetKey={filterResetKey}
                    handleDates={handleDates}
                    onSearch={onFilterSearch}
                    onReset={handleReset}
                    trailingActions={growthComparisonTrailingActions}
                    exports={[
                        {
                            label: strings.customermgmt_button_export,
                            onClick: () => {
                                if (selectedOrgs.length === 0) {
                                    setMessage({
                                        display: true,
                                        severity: 'error',
                                        message: strings.selectAtLeastOneOrg,
                                    });
                                    return;
                                }
                                handleClick();
                            },
                        },
                    ]}
                />
            </Box>

            {/* Section 2 — KPIs + booking status stripe */}
            <Box sx={{ mb: 3 }}>
                {overviewRowsEmpty ? (
                    <Paper elevation={0} sx={REPORT_EMPTY_SX}>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                            {!hasMappedOrgs
                                ? 'No mapped organizations to show revenue for.'
                                : 'No organizations match the current selection for this date range.'}
                        </Typography>
                    </Paper>
                ) : (
                    <ConsolidatedRevenueSummaryBanner
                        rows={revenueByOrgResponse?.organizations ?? []}
                        bookingStatusCounts={bookingStripeCounts}
                        loading={isRevenueOverviewLoading}
                        comparisonMode={comparisonMode}
                        actualizedPortfolio={
                            revenueByOrgResponse
                                ? {
                                      portfolioActualizedRevenue: revenueByOrgResponse.portfolioActualizedRevenue,
                                      portfolioActualizedRevenuePreviousPeriod:
                                          revenueByOrgResponse.portfolioActualizedRevenuePreviousPeriod,
                                      portfolioActualizedCumulativeTrend:
                                          revenueByOrgResponse.portfolioActualizedCumulativeTrend,
                                  }
                                : null
                        }
                    />
                )}
            </Box>

            {/* Section 3 — Leaderboard */}
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 1.5, color: 'text.primary' }}>Leaderboard</Typography>
                {overviewRowsEmpty ? (
                    <Paper elevation={0} sx={REPORT_EMPTY_SX}>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                            {!hasMappedOrgs
                                ? 'No mapped organizations to show revenue for.'
                                : 'No organizations match the current selection for this date range.'}
                        </Typography>
                    </Paper>
                ) : (
                    <ConsolidatedRevenueByOrgTable
                        rows={revenueByOrgResponse?.organizations ?? []}
                        loading={isRevenueOverviewLoading}
                        comparisonMode={comparisonMode}
                        insights={leaderboardInsights}
                    />
                )}
            </Box>

            {/* Section 4 — Budget vs Business on Books */}
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 1.5, color: 'text.primary' }}>
                    Budget vs Business on Books
                </Typography>
                {!pageResponse ? (
                    <Paper elevation={0} sx={REPORT_EMPTY_SX}>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                            Run search with your date range to load budget vs business-on-books.
                        </Typography>
                    </Paper>
                ) : pageResponse?.monthlyBudgetReport?.datasets?.length > 0 ? (
                    <Box sx={reportChartCellFullWidthSx}>
                        <BudgetOverviewChart
                            bobData={pageResponse.monthlyBudgetReport}
                            actualData={pageResponse.budgetVersusActualReport}
                            reportDateRange={{ startDate: dates.startDate, endDate: dates.endDate }}
                            handleExport={() => handleExport(pageResponse?.monthlyBudgetReport?.reportType)}
                        />
                    </Box>
                ) : (
                    <Paper elevation={0} sx={REPORT_EMPTY_SX}>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                            No budget vs business-on-books data for this range.
                        </Typography>
                    </Paper>
                )}
            </Box>

            <PortfolioMixAnalysis
                segmentBob={pageResponse?.eventTypeWiseBobReport}
                sourceRevenue={pageResponse?.agentWiseRevenueReport}
                onExportSegment={() => handleExport(pageResponse?.eventTypeWiseBobReport?.reportType)}
                onExportSource={() => handleExport(pageResponse?.agentWiseRevenueReport?.reportType)}
            />

            {/* Section 5 — other charts */}
            <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 1.5, color: 'text.primary' }}>Portfolio trends</Typography>
                {!pageResponse ? (
                    <Paper elevation={0} sx={REPORT_EMPTY_SX}>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                            Run search with your date range to load company-wise business on books and actualization by source.
                        </Typography>
                    </Paper>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6} sx={reportChartGridItemSx}>
                            {pageResponse?.companyWiseBobReport?.datasets?.length > 0 ? (
                                <Box sx={reportChartCellInGridSx}>
                                    <CompanyWiseBobChart
                                        data={pageResponse.companyWiseBobReport}
                                        name={pageResponse.companyWiseBobReport.name}
                                        handleExport={() => handleExport(pageResponse?.companyWiseBobReport?.reportType)}
                                        handleEnlarge={() => setEnlargedPortfolioChart('company')}
                                    />
                                </Box>
                            ) : (
                                <Paper elevation={0} sx={{ ...REPORT_EMPTY_SX, minHeight: 220, height: '100%' }}>
                                    <Typography sx={{ fontWeight: 800, mb: 0.75 }}>Company-wise Business on Books</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                                        No company-wise chart data for this range.
                                    </Typography>
                                </Paper>
                            )}
                        </Grid>
                        <Grid item xs={12} md={6} sx={reportChartGridItemSx}>
                            {pageResponse?.sourceWiseActualizationReport?.datasets?.length > 0 ? (
                                <Box sx={reportChartCellInGridSx}>
                                    <SourceActualizationProgress
                                        data={pageResponse.sourceWiseActualizationReport}
                                        name={pageResponse.sourceWiseActualizationReport.name}
                                        handleExport={() => handleExport(pageResponse?.sourceWiseActualizationReport?.reportType)}
                                        handleEnlarge={() => setEnlargedPortfolioChart('actualization')}
                                    />
                                </Box>
                            ) : (
                                <Paper elevation={0} sx={{ ...REPORT_EMPTY_SX, minHeight: 220, height: '100%' }}>
                                    <Typography sx={{ fontWeight: 800, mb: 0.75 }}>Actualization by Source</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                                        No actualization-by-source data for this range.
                                    </Typography>
                                </Paper>
                            )}
                        </Grid>
                    </Grid>
                )}
            </Box>

            {pageResponse && (
                <>
                    <ReportChartEnlargeDialog
                        open={enlargedPortfolioChart === 'company'}
                        onClose={() => setEnlargedPortfolioChart(null)}
                        title={pageResponse.companyWiseBobReport?.name || 'Company-wise Business on Books'}
                        headerActions={
                            pageResponse.companyWiseBobReport?.reportType ? (
                                <Tooltip title="Export" placement="bottom">
                                    <IconButton
                                        size="small"
                                        aria-label="Export"
                                        onClick={() =>
                                            handleExport(String(pageResponse.companyWiseBobReport.reportType))
                                        }>
                                        <FileDownloadOutlined fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            ) : null
                        }>
                        {pageResponse.companyWiseBobReport?.datasets?.length > 0 ? (
                            <CompanyWiseBobChart
                                key="enlarge-company-bob"
                                data={pageResponse.companyWiseBobReport}
                                name={pageResponse.companyWiseBobReport.name}
                                embeddedInDialog
                                showButton={false}
                                handleExport={() => {}}
                                handleEnlarge={() => {}}
                            />
                        ) : null}
                    </ReportChartEnlargeDialog>
                    <ReportChartEnlargeDialog
                        open={enlargedPortfolioChart === 'actualization'}
                        onClose={() => setEnlargedPortfolioChart(null)}
                        title={pageResponse.sourceWiseActualizationReport?.name || 'Actualization by Source'}
                        headerActions={
                            pageResponse.sourceWiseActualizationReport?.reportType ? (
                                <Tooltip title="Export" placement="bottom">
                                    <IconButton
                                        size="small"
                                        aria-label="Export"
                                        onClick={() =>
                                            handleExport(String(pageResponse.sourceWiseActualizationReport.reportType))
                                        }>
                                        <FileDownloadOutlined fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            ) : null
                        }>
                        {pageResponse.sourceWiseActualizationReport?.datasets?.length > 0 ? (
                            <SourceActualizationProgress
                                key="enlarge-actualization"
                                data={pageResponse.sourceWiseActualizationReport}
                                name={pageResponse.sourceWiseActualizationReport.name}
                                embeddedInDialog
                                showButton={false}
                                handleExport={() => {}}
                                handleEnlarge={() => {}}
                            />
                        ) : null}
                    </ReportChartEnlargeDialog>
                </>
            )}

            <Box sx={{ mb: 3 }}>
                <ContributionTrendsChart
                    data={contributionTrends}
                    loading={isRevenueOverviewLoading}
                    emptyMessage={
                        overviewRowsEmpty
                            ? !hasMappedOrgs
                                ? 'No mapped organizations to show contribution trends for.'
                                : 'No organizations match the current selection for this date range.'
                            : 'No monthly contribution data in this range for the selected organizations.'
                    }
                />
            </Box>

            <OrganizationHealthMatrix
                rows={revenueByOrgResponse?.organizations ?? []}
                loading={isRevenueOverviewLoading}
                comparisonMode={comparisonMode}
            />
        </Container>
    );
};

export default ConsolidatedReports;
